import React, { useState, useEffect } from 'react'
import { Link as RouterLink } from 'react-router-dom'
import {
  Box,
  Grid,
  Paper,
  Typography,
  List,
  ListItem,
  ListItemText,
  Chip,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Link,
  Collapse,
  IconButton,
  Button,
  TextField,
  Switch,
  Checkbox,
  FormControlLabel,
  FormGroup,
  FormHelperText,
} from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import EditIcon from '@mui/icons-material/Edit'
import { activitiesAPI, tracksAPI, patientsAPI } from '../services/api'
import { useAuth } from '../contexts/AuthContext'
import IconPicker from '../components/presentation/IconPicker'
import PathwayIcon from '../components/presentation/PathwayIcon'

const SENSITIVITY_COLORS = { low: 'success', medium: 'warning', high: 'error' }
const STATUS_COLORS = { covered: 'success', declined: 'default', deferred: 'warning' }

// data-service routes/activities.js ENTRY_MODES
const ENTRY_MODES = [
  { value: 'scheduled', label: 'Scheduled', hint: 'delivered by an activity pathway on the agenda' },
  { value: 'patient_initiated', label: 'Patient-initiated', hint: 'launchable from the exercise picker when assigned to the patient\'s intervention' },
]

/** Editable subset of a definition. The picker category is per intervention, not here. */
function toForm(activity) {
  const ui = activity?.ui && typeof activity.ui === 'object' ? activity.ui : {}
  return {
    name: activity?.name || '',
    description: activity?.description || '',
    entry_modes: Array.isArray(activity?.entry_modes) ? activity.entry_modes : [],
    is_active: activity?.is_active !== false,
    tile_title: ui.tile_title || '',
    tile_subtitle: ui.tile_subtitle || '',
    icon: ui.icon || null,
  }
}

/** PUT body: definition fields plus `ui` with other keys preserved. */
function toPayload(activity, form) {
  const ui = { ...(activity?.ui && typeof activity.ui === 'object' ? activity.ui : {}) }
  const setOrDrop = (key, value) => {
    if (value) ui[key] = value
    else delete ui[key]
  }
  setOrDrop('tile_title', form.tile_title.trim())
  setOrDrop('tile_subtitle', form.tile_subtitle.trim())
  setOrDrop('icon', form.icon)
  return {
    name: form.name.trim(),
    description: form.description.trim() || null,
    entry_modes: form.entry_modes,
    is_active: form.is_active,
    ui,
  }
}

/**
 * Activities: definitions (editable: name, description, entry modes,
 * active flag and the app tile copy), content banks, which pathways
 * deliver them, and per-patient coverage as recorded by the conversation
 * agent. Content banks and behavior prompts stay in the API/SQL for now;
 * which intervention offers an activity, and under which exercise
 * category, is set on the intervention page.
 */
function Activities() {
  const { hasPermission } = useAuth()
  // The client permission map has no `activities` resource; the server
  // gates PUT /activities on activities:update, which the same roles that
  // edit tracks hold.
  const canEditActivity = hasPermission('tracks', 'update')
  const [activities, setActivities] = useState([])
  const [tracks, setTracks] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const [selectedKey, setSelectedKey] = useState(null)
  const [detail, setDetail] = useState(null)
  const [detailLoading, setDetailLoading] = useState(false)
  const [promptOpen, setPromptOpen] = useState(false)
  const [editing, setEditing] = useState(false)
  const [form, setForm] = useState(toForm(null))
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState(null)

  const [patients, setPatients] = useState([])
  const [coverageUserId, setCoverageUserId] = useState('')
  const [coverage, setCoverage] = useState(null)
  const [coverageLoading, setCoverageLoading] = useState(false)

  useEffect(() => {
    const fetchAll = async () => {
      try {
        setLoading(true)
        setError(null)
        const [actRes, tracksRes, enrolledPatients] = await Promise.all([
          activitiesAPI.getAll(true),
          tracksAPI.getAll({ includeInactive: 'true' }).catch(() => ({ data: [] })),
          // Admin-scoped patient list (getEnrollments returns data directly)
          patientsAPI.getEnrollments().catch(() => []),
        ])
        setActivities(actRes.data || [])
        setTracks(tracksRes.data || [])
        setPatients(Array.isArray(enrolledPatients) ? enrolledPatients : [])
      } catch (err) {
        console.error('Failed to load activities:', err)
        setError('Failed to load activities')
      } finally {
        setLoading(false)
      }
    }
    fetchAll()
  }, [])

  useEffect(() => {
    if (!selectedKey) return
    const fetchDetail = async () => {
      try {
        setDetailLoading(true)
        const res = await activitiesAPI.getByKey(selectedKey)
        setDetail(res.data)
      } catch (err) {
        console.error('Failed to load activity detail:', err)
        setDetail(null)
      } finally {
        setDetailLoading(false)
      }
    }
    setCoverage(null)
    setCoverageUserId('')
    setPromptOpen(false)
    setEditing(false)
    setSaveError(null)
    fetchDetail()
  }, [selectedKey])

  const startEditing = () => {
    setForm(toForm(detail))
    setSaveError(null)
    setEditing(true)
  }

  const setField = (key, value) => setForm((prev) => ({ ...prev, [key]: value }))

  const toggleEntryMode = (mode, checked) => {
    setForm((prev) => ({
      ...prev,
      entry_modes: checked
        ? [...new Set([...prev.entry_modes, mode])]
        : prev.entry_modes.filter((m) => m !== mode),
    }))
  }

  const formErrors = {}
  if (!form.name.trim()) formErrors.name = 'Name is required'
  if (form.entry_modes.length === 0) formErrors.entry_modes = 'Choose at least one entry mode'

  const handleSave = async () => {
    if (Object.keys(formErrors).length > 0 || !detail) return
    try {
      setSaving(true)
      setSaveError(null)
      const res = await activitiesAPI.update(detail.activity_key, toPayload(detail, form))
      const updated = res.data || {}
      // PUT returns the row without items; keep the loaded content bank.
      setDetail((prev) => ({ ...prev, ...updated, items: prev?.items || [] }))
      setActivities((prev) =>
        prev.map((a) => (a.activity_key === detail.activity_key ? { ...a, ...updated } : a)),
      )
      setEditing(false)
    } catch (err) {
      console.error('Failed to save activity:', err)
      setSaveError(err.response?.data?.error || err.message || 'Failed to save activity')
    } finally {
      setSaving(false)
    }
  }

  useEffect(() => {
    if (!selectedKey || !coverageUserId) return
    const fetchCoverage = async () => {
      try {
        setCoverageLoading(true)
        const [stateRes, nextRes] = await Promise.all([
          activitiesAPI.getUserState(selectedKey, coverageUserId),
          activitiesAPI.getUserNextItems(selectedKey, coverageUserId).catch(() => null),
        ])
        setCoverage({
          state: stateRes.data?.state || {},
          next: nextRes?.data || null,
        })
      } catch (err) {
        console.error('Failed to load coverage:', err)
        setCoverage(null)
      } finally {
        setCoverageLoading(false)
      }
    }
    fetchCoverage()
  }, [selectedKey, coverageUserId])

  const linkedTracks = detail
    ? tracks.filter((t) => t.type_config?.activity_key === detail.activity_key)
    : []

  const coveredEntries = coverage
    ? Object.entries(coverage.state.items_covered || {}).sort(
        ([, a], [, b]) => String(a.covered_at).localeCompare(String(b.covered_at)),
      )
    : []

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
        <CircularProgress />
      </Box>
    )
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Box>
          <Typography variant="h5">Activities</Typography>
          <Typography variant="body2" color="text.secondary">
            Conversational behaviors delivered from content banks. Definitions and app tile copy
            are editable here; content banks are managed via the API for now. Which
            interventions offer an activity, and its picker category, are set per intervention.
          </Typography>
        </Box>
      </Box>

      {error && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Grid container spacing={2}>
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase' }}>
              Defined Activities
            </Typography>
            <List dense>
              {activities.map((a) => (
                <ListItem
                  key={a.activity_key}
                  button
                  selected={selectedKey === a.activity_key}
                  onClick={() => setSelectedKey(a.activity_key)}
                >
                  <ListItemText
                    primary={a.name}
                    secondary={a.activity_key}
                    secondaryTypographyProps={{ sx: { fontFamily: 'monospace', fontSize: 12 } }}
                  />
                  {!a.is_active && <Chip label="inactive" size="small" />}
                </ListItem>
              ))}
              {activities.length === 0 && (
                <Typography variant="body2" color="text.secondary" sx={{ p: 1 }}>
                  No activities defined
                </Typography>
              )}
            </List>
          </Paper>
        </Grid>

        <Grid item xs={12} md={8}>
          {!detail && !detailLoading && (
            <Paper sx={{ p: 3 }}>
              <Typography color="text.secondary">
                Select an activity to view its content bank, linked pathways, and patient coverage
              </Typography>
            </Paper>
          )}
          {detailLoading && (
            <Paper sx={{ p: 3, display: 'flex', justifyContent: 'center' }}>
              <CircularProgress size={28} />
            </Paper>
          )}
          {detail && !detailLoading && (
            <Box display="flex" flexDirection="column" gap={2}>
              {/* Definition */}
              <Paper sx={{ p: 2 }}>
                {editing ? (
                  <Box display="flex" flexDirection="column" gap={2}>
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                      <Typography variant="body2" sx={{ fontFamily: 'monospace' }} color="text.secondary">
                        {detail.activity_key}
                      </Typography>
                      <FormControlLabel
                        control={
                          <Switch
                            size="small"
                            checked={form.is_active}
                            onChange={(e) => setField('is_active', e.target.checked)}
                            disabled={saving}
                          />
                        }
                        label={<Typography variant="body2">{form.is_active ? 'Active' : 'Inactive'}</Typography>}
                      />
                    </Box>
                    {saveError && (
                      <Alert severity="error" onClose={() => setSaveError(null)}>
                        {saveError}
                      </Alert>
                    )}
                    <TextField
                      size="small"
                      label="Name"
                      value={form.name}
                      onChange={(e) => setField('name', e.target.value)}
                      error={Boolean(formErrors.name)}
                      helperText={formErrors.name}
                      disabled={saving}
                      fullWidth
                    />
                    <TextField
                      size="small"
                      label="Description"
                      value={form.description}
                      onChange={(e) => setField('description', e.target.value)}
                      multiline
                      rows={2}
                      disabled={saving}
                      fullWidth
                    />
                    <Box>
                      <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase' }}>
                        Entry modes
                      </Typography>
                      <FormGroup row>
                        {ENTRY_MODES.map((mode) => (
                          <FormControlLabel
                            key={mode.value}
                            control={
                              <Checkbox
                                size="small"
                                checked={form.entry_modes.includes(mode.value)}
                                onChange={(e) => toggleEntryMode(mode.value, e.target.checked)}
                                disabled={saving}
                              />
                            }
                            label={
                              <Box>
                                <Typography variant="body2">{mode.label}</Typography>
                                <Typography variant="caption" color="text.secondary">{mode.hint}</Typography>
                              </Box>
                            }
                            sx={{ mr: 3, alignItems: 'flex-start', mt: 0.5 }}
                          />
                        ))}
                      </FormGroup>
                      {formErrors.entry_modes && (
                        <FormHelperText error>{formErrors.entry_modes}</FormHelperText>
                      )}
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase' }}>
                        App tile
                      </Typography>
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                        How the activity appears in the app's exercise picker and library, in every
                        intervention that assigns it.
                      </Typography>
                      <Box display="flex" gap={2} flexWrap="wrap" alignItems="flex-start">
                        <TextField
                          size="small"
                          label="Tile title"
                          value={form.tile_title}
                          onChange={(e) => setField('tile_title', e.target.value)}
                          placeholder={form.name}
                          disabled={saving}
                          sx={{ flex: 1, minWidth: 200 }}
                        />
                        <TextField
                          size="small"
                          label="Tile subtitle"
                          value={form.tile_subtitle}
                          onChange={(e) => setField('tile_subtitle', e.target.value)}
                          disabled={saving}
                          sx={{ flex: 1, minWidth: 200 }}
                        />
                        <Box>
                          <IconPicker
                            value={form.icon}
                            onChange={(icon) => setField('icon', icon)}
                            disabled={saving}
                            helperText="Optional; the category icon is used when blank"
                          />
                        </Box>
                      </Box>
                    </Box>
                    <Box display="flex" justifyContent="flex-end" gap={1}>
                      <Button size="small" onClick={() => setEditing(false)} disabled={saving}>
                        Cancel
                      </Button>
                      <Button
                        size="small"
                        variant="contained"
                        onClick={handleSave}
                        disabled={saving || Object.keys(formErrors).length > 0}
                      >
                        {saving ? 'Saving...' : 'Save'}
                      </Button>
                    </Box>
                  </Box>
                ) : (
                  <>
                    <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                      <Box>
                        <Typography variant="h6">{detail.name}</Typography>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace' }} color="text.secondary">
                          {detail.activity_key}
                        </Typography>
                      </Box>
                      <Box display="flex" gap={1} alignItems="center">
                        {(detail.entry_modes || []).map((m) => (
                          <Chip key={m} label={m} size="small" variant="outlined" />
                        ))}
                        <Chip
                          label={detail.is_active ? 'active' : 'inactive'}
                          color={detail.is_active ? 'success' : 'default'}
                          size="small"
                        />
                        {canEditActivity && (
                          <IconButton size="small" onClick={startEditing} aria-label="Edit activity">
                            <EditIcon fontSize="small" />
                          </IconButton>
                        )}
                      </Box>
                    </Box>
                    {detail.description && (
                      <Typography variant="body2" sx={{ mt: 1 }}>
                        {detail.description}
                      </Typography>
                    )}
                    <Box display="flex" alignItems="center" gap={1} mt={1.5}>
                      <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase' }}>
                        App tile
                      </Typography>
                      {detail.ui?.icon && <PathwayIcon name={detail.ui.icon} size={16} />}
                      <Typography variant="body2">
                        {detail.ui?.tile_title || detail.name}
                        {detail.ui?.tile_subtitle ? ` — ${detail.ui.tile_subtitle}` : ''}
                      </Typography>
                      {!detail.ui?.tile_title && (
                        <Typography variant="caption" color="text.secondary">(falls back to the name)</Typography>
                      )}
                    </Box>
                  </>
                )}
                <Box display="flex" gap={1} mt={1.5}>
                  <Chip
                    size="small"
                    label={`${detail.config?.items_per_session ?? 3} items / session`}
                  />
                  {detail.config?.sensitivity_gate?.high_after_covered != null && (
                    <Chip
                      size="small"
                      label={`high items unlock after ${detail.config.sensitivity_gate.high_after_covered} covered`}
                    />
                  )}
                </Box>

                <Box mt={2}>
                  <Box display="flex" alignItems="center">
                    <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase' }}>
                      Behavior Prompt
                    </Typography>
                    <IconButton size="small" onClick={() => setPromptOpen(!promptOpen)}>
                      {promptOpen ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                    </IconButton>
                  </Box>
                  <Collapse in={promptOpen}>
                    <Paper variant="outlined" sx={{ p: 1.5, bgcolor: 'grey.50' }}>
                      <Typography
                        variant="body2"
                        component="pre"
                        sx={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace', fontSize: 12, m: 0 }}
                      >
                        {detail.behavior_prompt}
                      </Typography>
                    </Paper>
                  </Collapse>
                </Box>
              </Paper>

              {/* Linked pathways */}
              <Paper sx={{ p: 2 }}>
                <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase' }}>
                  Delivered by Pathways
                </Typography>
                {linkedTracks.length === 0 ? (
                  <Alert severity="info" sx={{ mt: 1 }}>
                    No pathway references this activity yet — it will not be scheduled into any
                    session agenda until an activity-type pathway binds it (and that pathway is
                    attached to an intervention).
                  </Alert>
                ) : (
                  <List dense>
                    {linkedTracks.map((t) => (
                      <ListItem key={t.id}>
                        <ListItemText
                          primary={
                            <Link component={RouterLink} to={`/tracks/${t.id}/edit`} underline="hover">
                              {t.name}
                            </Link>
                          }
                          secondary={
                            t.type_config?.item_tags?.length
                              ? `${t.code} — tag filter: ${t.type_config.item_tags.join(', ')}`
                              : t.code
                          }
                        />
                        <Chip
                          label={t.is_active ? 'active' : 'inactive'}
                          color={t.is_active ? 'success' : 'default'}
                          size="small"
                        />
                      </ListItem>
                    ))}
                  </List>
                )}
              </Paper>

              {/* Content bank */}
              <Paper sx={{ p: 2 }}>
                <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase' }}>
                  Content Bank ({(detail.items || []).length} items)
                </Typography>
                <Table size="small" sx={{ mt: 1 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell>#</TableCell>
                      <TableCell>Item</TableCell>
                      <TableCell>Question</TableCell>
                      <TableCell>Tags</TableCell>
                      <TableCell>Sensitivity</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(detail.items || []).map((item) => (
                      <TableRow key={item.item_key} sx={item.is_active ? {} : { opacity: 0.5 }}>
                        <TableCell>{item.position}</TableCell>
                        <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>
                          {item.item_key}
                          {!item.is_active && ' (inactive)'}
                        </TableCell>
                        <TableCell>{item.content?.question}</TableCell>
                        <TableCell>
                          {(item.tags || []).map((tag) => (
                            <Chip key={tag} label={tag} size="small" sx={{ mr: 0.5 }} />
                          ))}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={item.sensitivity}
                            color={SENSITIVITY_COLORS[item.sensitivity] || 'default'}
                            size="small"
                            variant="outlined"
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Paper>

              {/* Per-patient coverage — verify what the agent recorded */}
              <Paper sx={{ p: 2 }}>
                <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase' }}>
                  Patient Coverage
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                  What the conversation agent has recorded for a patient, and which items it would
                  fetch next.
                </Typography>
                <FormControl size="small" sx={{ minWidth: 280 }}>
                  <InputLabel>Patient</InputLabel>
                  <Select
                    value={coverageUserId}
                    label="Patient"
                    onChange={(e) => setCoverageUserId(e.target.value)}
                  >
                    {(patients || []).map((p) => (
                      <MenuItem key={p.id} value={p.id}>
                        {p.name || 'Unnamed'} — {String(p.id).slice(0, 8)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {coverageLoading && (
                  <Box display="flex" justifyContent="center" p={2}>
                    <CircularProgress size={24} />
                  </Box>
                )}

                {coverage && !coverageLoading && (
                  <Box mt={2}>
                    {coveredEntries.length === 0 ? (
                      <Alert severity="info">No items recorded for this patient yet.</Alert>
                    ) : (
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Item</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell>Recorded</TableCell>
                            <TableCell>Agent&apos;s summary of patient response</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {coveredEntries.map(([itemKey, entry]) => (
                            <TableRow key={itemKey}>
                              <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>{itemKey}</TableCell>
                              <TableCell>
                                <Chip
                                  label={entry.status}
                                  color={STATUS_COLORS[entry.status] || 'default'}
                                  size="small"
                                />
                              </TableCell>
                              <TableCell>
                                {entry.covered_at ? new Date(entry.covered_at).toLocaleString() : '—'}
                              </TableCell>
                              <TableCell>{entry.response_summary || '—'}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    )}

                    {coverage.next && (
                      <Box mt={2}>
                        <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase' }}>
                          Next up for this patient ({coverage.next.coverage?.remaining_count ?? '?'} remaining,
                          {' '}{coverage.next.coverage?.covered_count ?? '?'} covered)
                        </Typography>
                        <Box display="flex" gap={0.5} flexWrap="wrap" mt={0.5}>
                          {(coverage.next.items || []).map((item) => (
                            <Chip
                              key={item.item_key}
                              label={item.item_key}
                              size="small"
                              variant="outlined"
                              color={SENSITIVITY_COLORS[item.sensitivity] || 'default'}
                            />
                          ))}
                        </Box>
                      </Box>
                    )}
                  </Box>
                )}
              </Paper>
            </Box>
          )}
        </Grid>
      </Grid>
    </Box>
  )
}

export default Activities
